import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";

import type { TimeWindow, TimeZoneMode } from "@/shared/components/Timeline";
import { useDebouncedCallback } from "@/shared/hooks";

import {
  FILTERS_PARAM,
  type OverviewFilters,
  type OverviewUrl,
  parseOverviewUrl,
  readFilters,
  writeOverviewUrl,
} from "./overviewUrl";

/**
 * How long the window must hold still before it is written to the URL. A
 * drag reports a new window every pointer frame, and browsers refuse a
 * burst of `replaceState` calls (Safari throws past 100 in 30 seconds).
 */
export const WINDOW_WRITE_DELAY_MS = 250;

/** The keys to rewrite, or a function of the latest params that returns them. */
type UrlPatch =
  | Partial<OverviewUrl>
  | ((previous: URLSearchParams) => Partial<OverviewUrl>);

export interface OverviewUrlState {
  /** The selected session's id, `null` when none is selected. */
  session: string | null;
  /** Select a session; `null` clears. */
  setSession: (sessionId: string | null) => void;
  /** The clock times are read on; local when the link says nothing. */
  tz: TimeZoneMode;
  /** Remember the clock times are read on. Written at once: it changes on a click, not per frame. */
  setTz: (tz: TimeZoneMode) => void;
  /**
   * The user's filters; none when the link carries none or an unreadable
   * value. The same object until the `filters` key itself changes, so a pan
   * or a selection does not rebuild the query the filters feed.
   */
  filters: OverviewFilters;
  /**
   * Replace the filters with `update` applied to the ones in the URL
   */
  updateFilters: (
    update: (previous: OverviewFilters) => OverviewFilters,
  ) => void;
  /**
   * The window the link opened on, `undefined` when it carries none or once
   * the filters change. Read once, on mount: the windows `setTimeWindow`
   * writes afterwards do not change it.
   */
  initialTimeWindow: TimeWindow | undefined;
  /**
   * Remember the window. Written once it has held still for
   * `WINDOW_WRITE_DELAY_MS`; a write still pending when the tab unmounts is
   * dropped, since by then the location it would replace is another page's.
   */
  setTimeWindow: (timeWindow: TimeWindow) => void;
}

/**
 * The Overview tab's state kept in the URL, so a link reproduces what the
 * page showed. Every write is a `replace`: neither a selection nor a pan is
 * a place the Back button should return through. The query string's shape
 * lives in `overviewUrl.ts`.
 */
export function useOverviewUrlState(): OverviewUrlState {
  const [searchParams, setSearchParams] = useSearchParams();
  const { session, tz, timeWindow } = useMemo(
    () => parseOverviewUrl(searchParams),
    [searchParams],
  );
  const [initialTimeWindow, setInitialTimeWindow] = useState(
    () => timeWindow ?? undefined,
  );
  // Keyed on the raw text, so the filters keep their identity through every
  // other params change.
  const filtersText = searchParams.get(FILTERS_PARAM);
  const filters = useMemo(() => readFilters(filtersText), [filtersText]);

  const write = useCallback(
    (patch: UrlPatch) => {
      setSearchParams(
        (previous) =>
          writeOverviewUrl(
            previous,
            typeof patch === "function" ? patch(previous) : patch,
          ),
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setSession = useCallback(
    (sessionId: string | null) => write({ session: sessionId }),
    [write],
  );
  const setTz = useCallback(
    (next: TimeZoneMode) => write({ tz: next }),
    [write],
  );
  // `setSearchParams` closes over the params of the render it came from, so
  // a write that fires later must go through the latest one: a selection
  // made while a pan's write is pending would otherwise be overwritten by
  // the params from before it.
  const latestWrite = useRef(write);
  useEffect(() => {
    latestWrite.current = write;
  }, [write]);
  const setTimeWindow = useDebouncedCallback(
    useCallback(
      (next: TimeWindow) => latestWrite.current({ timeWindow: next }),
      [],
    ),
    WINDOW_WRITE_DELAY_MS,
  );

  // Applied to the filters in the latest params, not this render's.
  const updateFilters = useCallback(
    (update: (previous: OverviewFilters) => OverviewFilters) => {
      setInitialTimeWindow(undefined);
      // A window write still waiting out its delay belongs to the old
      // results; dropped, so it cannot land after the reset.
      setTimeWindow.cancel();
      write((previous) => ({
        filters: update(readFilters(previous.get(FILTERS_PARAM))),
        session: null,
        timeWindow: null,
      }));
    },
    [write, setTimeWindow],
  );

  return {
    session,
    setSession,
    tz,
    setTz,
    filters,
    updateFilters,
    initialTimeWindow,
    setTimeWindow,
  };
}
